import React, { useEffect, useMemo, useState } from 'react';
import { flatten, has } from 'lodash';
import { useQuestionnaireResultItemsQuery } from '../../../../graphql/graphql';
import useTranslation from '../../../../Hooks/useTranslation';
import { HealthDataFilterFormValues } from '../../../HealthData/Components/Filter/HealthDataFilterForm';
import { QuestionnaireItems } from '../../../HealthData/Enums/QuestionnaireItems.enum';
import { Table } from '../../../HealthData/Enums/Table.enum';
import useHealthData, {
	IHealthData,
} from '../../../HealthData/Hooks/useHealthData';
import { modifyQuestionnaireResultItemsToHealthData } from '../../../utils/dataModifications';
import HealthDataChart from '../../../HealthData/Components/Charts/HealthDataChart';
import { Chart } from '../../../HealthData/Enums/Chart.enum';
import HealthDataVerticalTable from '../../../HealthData/Components/Tables/HealthDataVerticalTable';

export const UNREGISTERED_KEYS = {
	painLevel: 'Označte na stupni od 0 do 10 stupeň Vaší bolesti',
};

interface MonitoringDataset {
	[QuestionnaireItems.HOW_MANY_TIMES_PATIENT_USE_RESCUE_MEDICATION]: IHealthData[];
	[QuestionnaireItems.PATIENT_SATISFACTION_WITH_TREATMENT]: IHealthData[];
	[QuestionnaireItems.CONNECTION_BETWEEN_PILLS_AND_PAIN]: IHealthData[];
	multiple: IHealthData[];
}

interface HealthDataKeys {
	[QuestionnaireItems.ACTUAL_WEIGHT]: string[];
	[QuestionnaireItems.CTG]: string[];
	[QuestionnaireItems.DIASTOLIC_BLOOD_PRESSURE]: string[];
	[QuestionnaireItems.SYSTOLIC_BLOOD_PRESSURE]: string[];
	[QuestionnaireItems.TOTAL_NUMBER_OF_PREGNANCIES]: string[];
	[QuestionnaireItems.WEIGHT_BEFORE_PREGNANCY]: string[];
	multiple: string[];
}

const datasetDefaults = {
	[QuestionnaireItems.ACTUAL_WEIGHT]: [],
	[QuestionnaireItems.CTG]: [],
	[QuestionnaireItems.DIASTOLIC_BLOOD_PRESSURE]: [],
	[QuestionnaireItems.SYSTOLIC_BLOOD_PRESSURE]: [],
	[QuestionnaireItems.TOTAL_NUMBER_OF_PREGNANCIES]: [],
	[QuestionnaireItems.WEIGHT_BEFORE_PREGNANCY]: [],
	multiple: [],
};

const TABLE_DATASET = {
	[Table.MULTIPLE]: {
		title: 'Multiple',
		keys: [
			QuestionnaireItems.HOW_MANY_TIMES_PATIENT_USE_RESCUE_MEDICATION,
			QuestionnaireItems.PATIENT_SATISFACTION_WITH_TREATMENT,
			QuestionnaireItems.CONNECTION_BETWEEN_PILLS_AND_PAIN,
			UNREGISTERED_KEYS.painLevel,
		],
	},
};

interface Props {
	patientId: string;
}

const QuestionnaireStats: React.FC<Props> = ({ patientId }) => {
	const { t } = useTranslation();
	const [dataset, setDataset] = useState<MonitoringDataset>(datasetDefaults);
	const { filterHealthDataByKeys } = useHealthData();

	const { data, isLoading } = useQuestionnaireResultItemsQuery({
		patientId,
	});

	const modifyDataset = () => {
		const filteredData = modifyQuestionnaireResultItemsToHealthData({
			items: data,
			t,
			patientId,
		});

		return {
			...filteredData,
			multiple: filterHealthDataByKeys(
				flatten(Object.values(filteredData)),
				TABLE_DATASET[Table.MULTIPLE].keys,
			),
		} as MonitoringDataset;
	};

	useEffect(() => {
		const modifiedDataset = modifyDataset();

		setDataset(modifiedDataset);
	}, [data]);

	const { maxDate, minDate } = useMemo(() => {
		const dates = flatten(
			dataset.multiple.map(({ data }) => data.map(({ time }) => time)),
		);

		return {
			//@ts-ignore
			maxDate: new Date(Math.max(...dates.map(date => new Date(date)))),
			//@ts-ignore
			minDate: new Date(Math.min(...dates.map(date => new Date(date)))),
		};
	}, [JSON.stringify(dataset.multiple)]);

	return (
		<div>
			{dataset.multiple.length > 0 && (
				<HealthDataVerticalTable
					tableName={Table.MULTIPLE}
					healthData={dataset.multiple}
					isLoading={isLoading}
					filter={{
						from: minDate.toString(),
						to: maxDate.toString(),
						view: 'year',
						source: [],
						keys: [
							QuestionnaireItems.HOW_MANY_TIMES_PATIENT_USE_RESCUE_MEDICATION,
							QuestionnaireItems.PATIENT_SATISFACTION_WITH_TREATMENT,
							QuestionnaireItems.CONNECTION_BETWEEN_PILLS_AND_PAIN,
						],
						type: 'textfield',
					}}
				/>
			)}
			{/*@ts-ignore */}
			{dataset?.[UNREGISTERED_KEYS.painLevel]?.length > 0 && (
				<HealthDataChart
					view='year'
					chartType={Chart.PAIN_LEVEL}
					//@ts-ignore
					healthData={dataset[UNREGISTERED_KEYS.painLevel]}
					isLoading={isLoading}
				/>
			)}
			{dataset?.[QuestionnaireItems.PATIENT_SATISFACTION_WITH_TREATMENT]
				?.length > 0 && (
				<HealthDataChart
					view='month'
					chartType={Chart.PATIENT_SATISFACTION_WITH_TREATMENT}
					healthData={
						dataset[QuestionnaireItems.PATIENT_SATISFACTION_WITH_TREATMENT]
					}
					isLoading={isLoading}
				/>
			)}
			{dataset?.[
				QuestionnaireItems.HOW_MANY_TIMES_PATIENT_USE_RESCUE_MEDICATION
			]?.length > 0 && (
				<HealthDataChart
					view='year'
					chartType={Chart.HOW_MANY_TIMES_PATIENT_USE_RESCUE_MEDICATION}
					healthData={
						dataset[
							QuestionnaireItems.HOW_MANY_TIMES_PATIENT_USE_RESCUE_MEDICATION
						]
					}
					isLoading={isLoading}
				/>
			)}
		</div>
	);
};

export default QuestionnaireStats;
